import { RefObject } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, X } from "lucide-react";
import { downloadKeywordTemplateCsv } from "@/utils/keywordInput";

interface AddKeywordFileUploadTabProps {
  themeName: string;
  fileInputRef: RefObject<HTMLInputElement>;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  uploadedFileName: string | null;
  uploadParsed: string[];
  onRemoveParsed: (phrase: string) => void;
}

export function AddKeywordFileUploadTab({
  themeName,
  fileInputRef,
  onFileUpload,
  uploadedFileName,
  uploadParsed,
  onRemoveParsed,
}: AddKeywordFileUploadTabProps) {
  return (
    <div className="flex flex-col flex-1 min-h-0 px-6 py-4 gap-3 overflow-y-auto">
      <div className="border-2 border-dashed rounded-lg p-6 text-center">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={onFileUpload}
          className="hidden"
          id="theme-keyword-file-upload"
        />
        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground mb-2">
          Upload a CSV or Excel file with keywords in Column A
        </p>
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline-on-white"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4" />
            Choose File
          </Button>
          <Button variant="ghost" size="sm" onClick={downloadKeywordTemplateCsv}>
            <Download className="h-4 w-4" />
            Download Template
          </Button>
        </div>
        {uploadedFileName && (
          <p className="text-sm text-primary mt-2">Uploaded: {uploadedFileName}</p>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Supported formats: CSV, XLSX, XLS. Keywords should be listed in Column A. New
        keywords are created in your Keyword Bank and added to the &ldquo;{themeName}&rdquo; theme
        once you click &ldquo;Add to {themeName}&rdquo; below.
      </p>

      {uploadParsed.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs text-muted-foreground">
            {uploadParsed.length} keyword{uploadParsed.length !== 1 ? "s" : ""} detected:
          </span>
          <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto">
            {uploadParsed.map((kw) => (
              <Badge key={kw} variant="secondary" className="text-xs pr-1">
                {kw}
                <button
                  onClick={() => onRemoveParsed(kw)}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
